import { Grid, Typography } from '@material-ui/core';
import moment from 'moment-timezone';
import React from 'react';

import { ABTestMode } from '~/src/dash/api/ab-test';
import {
  ABTestingActiveActive,
  createABTest,
  disableABTest,
  HeaderBidderPoddedRequestActiveActive,
  HeaderBidderPoddedRequestActiveInactive,
  IABTest,
  IABTesting,
  IHeaderBidder,
} from '~/src/dash/api/console-api';
import { FrequencyCapUnitDays, FrequencyCapUnitHours } from '~/src/dash/api/frequency-cap';
import { EBool } from '~/src/dash/api/types/bool';
import { ABTestPanel } from '~/src/dash/components/ab-test-panel';
import {
  DateTimeRangeComponent,
  IDateRangeChangeEvent,
} from '~/src/dash/components/datepicker/date-time-range-component';
import { IInputRow, InputRow } from '~/src/dash/components/input-row';
import ModelMissingPermission from '~/src/dash/components/model-missing-permission';
import { SearchDropdownSingle } from '~/src/dash/components/search-dropdown-single';
import { genTimezoneList } from '~/src/dash/globals/timezone';
import { ct, Row, Rows, spacing, Text, TextButton } from '~/src/dash/publicaUI';
import { IStore } from '~/src/dash/reducers/store';
import { IListItem, IMenuList } from '~/src/dash/types/menuList';
import {
  IReduxActionProps,
  ISessionStateProps,
  IUserStateProps,
  IWithStylesProps,
} from '~/src/dash/types/props';
import { formatDateFromUTCToOffset, formatDateWithOffsetToUTC } from '~/src/dash/utils/date';
import { decorate } from '~/src/dash/utils/decorator';
import { noopVoid } from '~/src/dash/utils/noop';
import { IS_AUTHORIZED } from '~/src/dash/utils/permission_service';
import { hasErrors, IErrors, IValidator, validatorFactory } from '~/src/dash/utils/validator';
import { SnackbarView } from '~/src/dash/views/snackbar-view';

interface IProps extends IWithStylesProps, IReduxActionProps, ISessionStateProps, IUserStateProps {
  bidder: IHeaderBidder;
  currentABTest: IABTesting;
  pastABTests: IABTesting[];
  getBidder: () => void;
  implementParams: (params: any) => void;
  updateCurrentlyWritingABTest: (status: boolean) => void;
  updateSnackbar: (message: string, error: boolean) => void;
  snackbarClose: () => void;
  snackbarError: boolean;
  snackbarMessage: string;
  updateTriggerTestLaunch: (status: boolean) => void;
  testLaunchTriggered: boolean;
  existing: boolean;
}

interface INumberBoolMap {
  [key: number]: boolean;
}

interface IState {
  testCollapsibleStatus: INumberBoolMap;
  errors: IErrors;

  // New Test Parameter Values
  NewBTest: {
    Name: string;
    // FrequencyCap: string;
    BidFloor: string;
    MinAdDurationOverride: string;
    MaxAdDurationOverride: string;
    AppBundleOverride: string;
    AppNameOverride: string;
    AppDomainOverride: string;
    Share: string;
    PoddedRequestActive: string;
    MultiBidActive: string;
    NumberOfAds: string;
    Pods: string;
    UseSyntheticIDActive: string;
    UseTruncatedIPActive: string;
  };
  NewTest: boolean;
  // Next variables handle Ab Test dates component
  dateStart?: moment.Moment;
  dateEnd?: moment.Moment;
  StartDate?: string;
  EndDate?: string;
  Timezone: string;
  timezonesList: {};
  FrequencyLimitDays: string;
  FrequencyLimitHours: string;
  frequencyLimitDuration: string;
  frequencyLimitUnit: string;

  // Existing Parameter Value
  NewATestShare: string;
}

const abTestValidationDatesSetting: IValidator = {
  StartDate(v: string): string {
    if (!v) {
      return 'Please add a start date for your test.';
    }
    return '';
  },
  EndDate(v: string): string {
    if (!v) {
      return 'Please add an end date for your test.';
    }
    return '';
  },
};

const abTestValidationSetting: IValidator = {
  Name(v: string): string {
    if (!v) {
      return 'Please choose a name for your test.';
    } else if (v === 'New Test Name') {
      return 'Please choose a custom name for your test.';
    }
    return '';
  },
  BidFloor(v: string): string {
    if (!v) {
      return 'Please enter a bid floor.';
    } else if (v.indexOf(',') > -1) {
      return 'Please use . instead of , for decimal.';
    } else if (isNaN(parseFloat(v))) {
      return 'Please input a valid decimal';
    } else if (parseFloat(v) < 0) {
      return 'Bid Floor must be $0 or higher.';
    }
    return '';
  },
  MaxAdDurationOverride(v: string): string {
    if (!v) {
      return 'Please input a valid number.';
    } else if (/[,.]/.test(v)) {
      return 'Please input a valid integer, not a decimal.';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number.';
    } else if (nbr < 0) {
      return 'Please input a positive number.';
    }
    return '';
  },
  MinAdDurationOverride(v: string): string {
    if (!v) {
      return 'Please input a valid number.';
    } else if (/[,.]/.test(v)) {
      return 'Please input a valid integer, not a decimal.';
    }
    const nbr = Number(v);
    if (isNaN(nbr)) {
      return 'Please input a valid number.';
    } else if (nbr < 0) {
      return 'Please input a positive number.';
    }
    return '';
  },
  Share(v: string): string {
    if (!v) {
      return 'Please input a share of requests to be allocated to the test.';
    }
    const nbr = parseInt(v, 10);
    if (isNaN(nbr)) {
      return 'Please input a valid number.';
    } else if (nbr <= 0) {
      return 'Share of requests allocated to test must be greater than 0%.';
    } else if (nbr >= 100) {
      return 'Share of requests allocated to test must be less than 100%.';
    }
    return '';
  },
};

export const BidderTests = decorate(
  {
    redux: (state: IStore) => {
      return {
        user: state.user,
        session: state.session,
      };
    },
    styles: () => ({
      insideAccordionText: {
        fontSize: 14,
        fontWeight: 400,
        color: '$434343',
      },
    }),
  },
  class extends React.Component<IProps, IState> {
    // Constructor
    constructor(props: IProps) {
      super(props);
      this.state = {
        testCollapsibleStatus: {} as INumberBoolMap,
        errors: {} as IErrors,

        // New Test Param Values
        NewTest: false,
        NewBTest: {
          Name: 'New Test Name',
          BidFloor: '',
          MinAdDurationOverride: '',
          MaxAdDurationOverride: '',
          AppBundleOverride: '',
          AppNameOverride: '',
          AppDomainOverride: '',
          Share: '50',
          PoddedRequestActive: '',
          MultiBidActive: '1',
          NumberOfAds: '1',
          Pods: '',
          UseSyntheticIDActive: '',
          UseTruncatedIPActive: '',
        },
        // dateStart and dateEnd are used to handle dates internally on format moment.Moment
        dateStart: undefined,
        dateEnd: undefined,
        // StartDate and EndDate are used to send the date to the BackEnd on String format
        StartDate: undefined,
        EndDate: undefined,
        Timezone: this.props.session.publisher.Timezone,
        timezonesList: {} as IMenuList,
        FrequencyLimitDays: '30',
        FrequencyLimitHours: '0',
        frequencyLimitDuration: '0',
        frequencyLimitUnit: FrequencyCapUnitDays,
        // Existing Bidder Param Share Value
        NewATestShare: '50',
      };
    }

    componentDidMount = () => {
      const collapseStatus = {} as INumberBoolMap;
      this.props.bidder.abTests.forEach((test: any) => {
        collapseStatus[test.ID] = false;
      });

      const updatedBTest = Object.assign({}, this.state.NewBTest, {
        BidFloor: String(this.props.bidder.BidFloor),
        MinAdDurationOverride: String(this.props.bidder.MinAdDurationOverride),
        MaxAdDurationOverride: String(this.props.bidder.MaxAdDurationOverride),
        AppBundleOverride: String(this.props.bidder.AppBundleOverride),
        AppNameOverride: String(this.props.bidder.AppNameOverride),
        AppDomainOverride: String(this.props.bidder.AppDomainOverride),
        PoddedRequestActive: String(this.props.bidder.PoddedRequestActive),
        MultiBidActive: String(this.props.bidder.MultiBidActive),
        Pods: this.props.bidder.Slots.join(','),
        UseSyntheticIDActive: String(this.props.bidder.AnonymizeDeviceID),
        UseTruncatedIPActive: String(this.props.bidder.AnonymizeIP),
      });
      this.setState({
        testCollapsibleStatus: Object.assign({}, collapseStatus),
        NewBTest: updatedBTest,
      });
      this.genTimezoneList();
    };

    genTimezoneList = () => {
      const { session } = this.props;

      const { FrequencyLimitDays, FrequencyLimitHours } = this.state;

      const Timezone = session.publisher.Timezone;
      const timezonesList = genTimezoneList(Timezone);
      let frequencyLimitUnit: string = FrequencyCapUnitDays;
      let frequencyLimitDuration: string;
      if (FrequencyLimitDays && FrequencyLimitDays !== '0') {
        frequencyLimitUnit = FrequencyCapUnitDays;
        frequencyLimitDuration = FrequencyLimitDays;
      } else if (FrequencyLimitHours && FrequencyLimitHours !== '0') {
        frequencyLimitUnit = FrequencyCapUnitHours;
        frequencyLimitDuration = FrequencyLimitHours;
      } else {
        frequencyLimitUnit = FrequencyCapUnitDays;
        frequencyLimitDuration = '30';
      }
      this.setState({
        timezonesList,
        Timezone,
        frequencyLimitDuration,
        frequencyLimitUnit,
      });
    };

    componentDidUpdate = (prevProps: IProps) => {
      if (!prevProps.currentABTest && this.props.currentABTest) {
        this.setState({
          NewTest: false,
          NewBTest: {
            Name: 'New Test Name',
            BidFloor: this.props.bidder.BidFloor,
            MinAdDurationOverride: String(this.props.bidder.MinAdDurationOverride),
            MaxAdDurationOverride: String(this.props.bidder.MaxAdDurationOverride),
            AppBundleOverride: String(this.props.bidder.AppBundleOverride),
            AppNameOverride: String(this.props.bidder.AppNameOverride),
            AppDomainOverride: String(this.props.bidder.AppDomainOverride),
            Share: '0',
            PoddedRequestActive: String(this.props.bidder.PoddedRequestActive),
            MultiBidActive: String(this.props.bidder.MultiBidActive),
            NumberOfAds: '1',
            Pods: this.props.bidder.Slots.join(','),
            UseSyntheticIDActive: String(this.props.bidder.AnonymizeDeviceID),
            UseTruncatedIPActive: String(this.props.bidder.AnonymizeIP),
          },
          NewATestShare: '100',
        });
      }

      // Note: having a parent component trigger a function in a child component runs counter to good React design
      // The below will need to be refactored in V2 of ABTesting to lift up all testing functionality to the bidder settings parent component
      // It has been introduced only to solve V1 functionality needs quickly
      if (!prevProps.testLaunchTriggered && this.props.testLaunchTriggered) {
        this.launchTest();
      }
    };

    handleChange = ({ target }: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (target.name === 'Share') {
        const shareVal = target.value.split('%')[0];
        const val = shareVal === '' || !parseInt(shareVal, 10) ? '0' : shareVal;
        const shareDifference = String(100 - parseInt(val, 10));
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, { Share: shareVal }),
          NewATestShare: shareDifference,
        });
      } else if (target.name === 'FrequencyCap') {
        const capVal = target.value.replace(/,/g, '');
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, { FrequencyCap: capVal }),
        });
      } else if (target.name === 'BidFloor') {
        const parts = target.value.split('$');
        const decimalVal = parts.length > 1 ? parts[1] : parts[0];
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, { BidFloor: decimalVal }),
        });
      } else if (target.name === 'PoddedRequestActive') {
        const newValue =
          Object(target || { checked: false }).checked === true
            ? HeaderBidderPoddedRequestActiveActive
            : HeaderBidderPoddedRequestActiveInactive;
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, {
            PoddedRequestActive: newValue,
            MultiBidActive: EBool.EBool_FALSE,
            NumberOfAds: 8,
          }),
        });
      } else if (target.name === 'UseSyntheticIDActive') {
        const newValue =
          Object(target || { checked: false }).checked === true
            ? HeaderBidderPoddedRequestActiveActive
            : HeaderBidderPoddedRequestActiveInactive;
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, {
            UseSyntheticIDActive: newValue,
          }),
        });
      } else if (target.name === 'UseTruncatedIPActive') {
        const newValue =
          Object(target || { checked: false }).checked === true
            ? HeaderBidderPoddedRequestActiveActive
            : HeaderBidderPoddedRequestActiveInactive;
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, {
            UseTruncatedIPActive: newValue,
          }),
        });
      } else if (target.name === 'MultiBidActive') {
        const newValue =
          Object(target || { checked: false }).checked === true
            ? EBool.EBool_TRUE
            : EBool.EBool_FALSE;
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, {
            MultiBidActive: newValue,
            PoddedRequestActive: HeaderBidderPoddedRequestActiveInactive,
          }),
        });
      } else if (target.name === 'NumberOfAds') {
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, { ['NumberOfAds']: target.value }),
        });
      } else if (target.name === 'Pods') {
        if (target.value.includes('all') && this.state.NewBTest.Pods !== '') {
          // All has just been selected, so set the string to empty (= all)
          this.setState({
            NewBTest: Object.assign({}, this.state.NewBTest, { ['Pods']: '' }),
          });
        } else {
          // All hasn't been selected, just remove it from the array and store the value
          const targetedPodsString = (target.value as any as string[])
            .filter((e) => e !== 'all')
            .join(',');
          this.setState({
            NewBTest: Object.assign({}, this.state.NewBTest, { ['Pods']: targetedPodsString }),
          });
        }
      } else {
        this.setState({
          NewBTest: Object.assign({}, this.state.NewBTest, { [target.name]: target.value }),
        });
      }
    };

    handleCancel = () => {
      const newList = Object.assign({}, this.state.testCollapsibleStatus);
      newList[0] = false;

      this.setState({
        testCollapsibleStatus: newList,
      });
      setTimeout(() => {
        this.setState({
          NewTest: false,
          NewBTest: {
            Name: 'New Test Name',
            BidFloor: this.props.bidder.BidFloor,
            MinAdDurationOverride: String(this.props.bidder.MinAdDurationOverride),
            MaxAdDurationOverride: String(this.props.bidder.MaxAdDurationOverride),
            AppBundleOverride: String(this.props.bidder.AppBundleOverride),
            AppNameOverride: String(this.props.bidder.AppNameOverride),
            AppDomainOverride: String(this.props.bidder.AppDomainOverride),
            Share: '0',
            PoddedRequestActive: String(this.props.bidder.PoddedRequestActive),
            MultiBidActive: String(this.props.bidder.MultiBidActive),
            NumberOfAds: '1',
            Pods: this.props.bidder.Slots.join(','),
            UseSyntheticIDActive: String(this.props.bidder.AnonymizeDeviceID),
            UseTruncatedIPActive: String(this.props.bidder.AnonymizeIP),
          },
          NewATestShare: '100',
        });
      }, 800);

      this.props.updateTriggerTestLaunch(false);
      this.props.updateCurrentlyWritingABTest(false);
    };

    endTest = (index: number) => {
      disableABTest({
        query: {
          publisher_id: this.props.session.publisher.ID,
          bidder_id: this.props.bidder.ID,
          test_id: index,
        },
      })
        .then(() => {
          this.props.updateSnackbar('AB Test successfully ended.', false);
          this.props.getBidder();
        })
        .catch(() => {
          this.props.updateSnackbar('Error ending AB Test.', true);
        });
    };

    updateCollapseStatus = (index: number) => {
      const newList = Object.assign({}, this.state.testCollapsibleStatus);
      newList[index] = !newList[index];
      this.setState({
        testCollapsibleStatus: newList,
      });
    };

    implementParameters = (index: number) => {
      // Parse B test parameters and set on bidder object
      const test = this.props.bidder.abTests.find((test: IABTesting) => test.ID === index);
      if (test === undefined) {
        return;
      }

      const bTest = test.Tests.find((b: IABTest) => b.Name === 'B');
      if (bTest === undefined) {
        return;
      }

      let params;
      try {
        params = JSON.parse(bTest.Parameters);
      } catch (e) {
        params = {};
      }

      if (Object.keys(params).length === 0) {
        return;
      }

      // turn all parameter values into strings to pass bidder settings validations
      for (const [key, value] of Object.entries(params)) {
        params[key] = String(value);
      }
      this.props.implementParams(params);
    };

    setNewTest = () => {
      const newList = Object.assign({}, this.state.testCollapsibleStatus);
      newList[0] = true;

      this.setState({
        testCollapsibleStatus: newList,
        NewTest: true,
      });

      this.props.updateCurrentlyWritingABTest(true);
    };

    diffParameters = (bidderParams: any, newTestParams: any) => {
      const diffTestParams = Object.assign({}, newTestParams);

      for (const key in newTestParams) {
        if (newTestParams.hasOwnProperty(key) && !['Share', 'Name'].includes(key)) {
          if (bidderParams.hasOwnProperty(key)) {
            if (String(bidderParams[key]) === String(newTestParams[key])) {
              delete diffTestParams[key];
            }
          }
        }
      }

      return diffTestParams;
    };

    launchTest = () => {
      // turn off trigger test launch immediately
      this.props.updateTriggerTestLaunch(false);
      // use the validation factory to validate the test's fields
      const errors = validatorFactory(abTestValidationSetting)(this.state.NewBTest);
      if (this.state.StartDate === undefined || this.state.EndDate === undefined) {
        const dateErrors = validatorFactory(abTestValidationDatesSetting)(this.state);
        if (hasErrors(dateErrors)) {
          if (dateErrors.StartDate) {
            this.props.updateSnackbar(`${dateErrors.StartDate}`, true);
          } else {
            this.props.updateSnackbar(`${dateErrors.EndDate}`, true);
          }
          this.setState(() => {
            setTimeout(this.props.snackbarClose, 6000);
          });
          return;
        }
      }
      // if there is no error, check that the min ad duration is below the max ad duration
      if (
        !errors.MinAdDurationOverrideparseInt &&
        !errors.MaxAdDurationOverride &&
        parseInt(this.state.NewBTest.MinAdDurationOverride, 10) >
          parseInt(this.state.NewBTest.MaxAdDurationOverride, 10)
      ) {
        errors.MinAdDurationOverride = 'Min ad duration must be less than max ad duration.';
      }

      if (hasErrors(errors)) {
        if (errors.Name) {
          this.props.updateSnackbar(`${errors.Name}`, true);
        } else {
          this.props.updateSnackbar('Please fix errors before launching test', true);
        }
        this.setState(
          {
            errors,
          },
          () => {
            setTimeout(this.props.snackbarClose, 6000);
          },
        );
        return;
      }

      // Check what params have changed?
      const testParams = {
        BidFloor: parseFloat(this.state.NewBTest.BidFloor),
        AppBundleOverride: this.state.NewBTest.AppBundleOverride,
        AppNameOverride: this.state.NewBTest.AppNameOverride,
        AppDomainOverride: this.state.NewBTest.AppDomainOverride,
        // FrequencyCap: parseInt(this.state.NewBTest.FrequencyCap, 10),
        MinAdDurationOverride: parseInt(this.state.NewBTest.MinAdDurationOverride, 10),
        MaxAdDurationOverride: parseInt(this.state.NewBTest.MaxAdDurationOverride, 10),
        PoddedRequestActive: parseInt(this.state.NewBTest.PoddedRequestActive, 10),
        MultiBidActive: parseInt(this.state.NewBTest.MultiBidActive, 10),
        NumberOfAds: parseInt(this.state.NewBTest.NumberOfAds, 10),
        Pods: this.state.NewBTest.Pods,
        UseSyntheticIDActive: parseInt(this.state.NewBTest.UseSyntheticIDActive, 10),
        UseTruncatedIPActive: parseInt(this.state.NewBTest.UseTruncatedIPActive, 10),
      };

      const newParameters = this.diffParameters(this.props.bidder, testParams);

      if (Object.keys(newParameters).length === 0) {
        this.props.updateSnackbar('Please define at least one parameter on your test.', true);
        setTimeout(this.props.snackbarClose, 6000);
        return;
      }

      const parsedParams = JSON.stringify(newParameters);
      const newTestObj = {
        Name: this.state.NewBTest.Name,
        Active: ABTestingActiveActive,
        BidderID: parseInt(this.props.bidder.ID, 10),
        ID: 0, // This field should be set on the backend, this is a placeholder
        StartDate: this.state.StartDate,
        EndDate: this.state.EndDate,
        Tests: [
          {
            Name: 'B',
            ID: 0, // This field should be set on the backend, this is a placeholder
            Parameters: parsedParams,
            Share: parseInt(this.state.NewBTest.Share, 10),
          },
        ],
      };

      createABTest(newTestObj, this.props.session.publisher.ID, this.props.bidder.ID).then(
        (res) => {
          const err = Array.isArray(res) ? res[1] : null;
          if (err) {
            this.props.updateSnackbar('Error creating AB Test.', true);
          } else {
            this.props.updateSnackbar('AB Test successfully created.', false);
            // if successfully launched, set currently writing test to false
            this.props.updateCurrentlyWritingABTest(false);
            this.props.getBidder();
          }
        },
      );
    };

    paramsDisabled() {
      return this.props.existing && this.hasNoWriteRights();
    }

    setStateCallback = (keys: string[], values: any[], setStateCallbacks: Array<() => void>) => {
      const stateObj: any = {} as IState;
      for (let i = 0; i < keys.length; i++) {
        stateObj[keys[i]] = values[i];
      }
      this.setState(stateObj, () => {
        setStateCallbacks.forEach((callback: () => void) => callback());
      });
    };

    handleChangeDate = ({ start, end }: Partial<IDateRangeChangeEvent>) => {
      this.setState({
        dateStart: start,
        dateEnd: end,
        StartDate: start
          ? formatDateWithOffsetToUTC(start, this.props.session.publisher.Timezone)
          : undefined,
        EndDate: end
          ? formatDateWithOffsetToUTC(end, this.props.session.publisher.Timezone)
          : undefined,
      });
    };

    handleTimezoneAcceptClick = (): void => {
      for (const { title, selected } of Object.values(this.state.timezonesList) as IListItem[]) {
        if (selected) {
          this.setState({
            Timezone: title,
          });
          break;
        }
      }
    };

    hasNoWriteRights = (): boolean => {
      return !IS_AUTHORIZED(['write_bidder'], this.props.user.user).isAuthorized;
    };

    getTimezone = () => {
      return this.state.Timezone === ''
        ? this.props.session.publisher.Timezone
        : this.state.Timezone;
    };

    renderFlighting = (abTest: IABTesting) => {
      const { classes } = this.props;
      const { dateStart, dateEnd, timezonesList } = this.state;

      const renderFlightDates = () => (
        <Grid item>
          <Grid container alignItems='center' wrap='nowrap' spacing={1}>
            <Grid item xs={2} style={{ flexShrink: 0 }}>
              <Grid container direction='row' justify='flex-start' alignItems='center' spacing={0}>
                <Grid item style={{ paddingTop: '20px' }}>
                  <Typography className={classes.insideAccordionText}>Flight Dates</Typography>
                </Grid>
              </Grid>
            </Grid>
            <Grid item xs={10}>
              <Grid container direction='row' alignItems='center' spacing={1}>
                <Grid item xs={8}>
                  <DateTimeRangeComponent
                    timezone={this.getTimezone()}
                    start={
                      abTest.StartDate
                        ? abTest.StartDate === 'null'
                          ? undefined
                          : formatDateFromUTCToOffset(
                              abTest.StartDate,
                              this.props.session.publisher.Timezone,
                            )
                        : dateStart
                    }
                    end={
                      abTest.EndDate
                        ? abTest.EndDate === 'null'
                          ? undefined
                          : formatDateFromUTCToOffset(
                              abTest.EndDate,
                              this.props.session.publisher.Timezone,
                            )
                        : dateEnd
                    }
                    onChange={(e) => this.handleChangeDate(e)}
                    enablePresets={true}
                  />
                </Grid>
                <Grid item xs={3} style={{ paddingTop: '20px' }}>
                  <SearchDropdownSingle
                    menuTitle={'Timezone'}
                    list={timezonesList}
                    onChange={this.setStateCallback}
                    stateKey={'timezonesList'}
                    menuAcceptClickCallbacks={[]}
                    handleMenuAcceptClick={this.handleTimezoneAcceptClick}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      );

      const flightDatesRow = {
        renderType: 'custom',
        customRender: renderFlightDates,
        title1: '',
        field1: '',
        value1: this.state.dateStart,
        defaultValue1: undefined,
        handleChange1: noopVoid,
        value2: this.state.dateEnd,
        defaultValue2: undefined,
        isActiveValue1: () => false,
        getActiveTag1: () => '',
      } as IInputRow;

      return (
        <Grid item style={{ paddingLeft: 20, paddingTop: 36 }}>
          <Grid container direction='column'>
            <InputRow rowInfo={flightDatesRow} disableEdit={this.paramsDisabled()} />
          </Grid>
        </Grid>
      );
    };

    // Rendering
    render() {
      const { currentABTest, pastABTests } = this.props;

      let showNewTestButton = true;
      let showCurrentTest = false;
      if (this.state.NewTest) {
        showNewTestButton = false;
      }
      if (currentABTest && Object.keys(currentABTest).length !== 0) {
        showNewTestButton = false;
        if (!this.state.NewTest) {
          showCurrentTest = true;
        }
      }

      const readAuthorization = IS_AUTHORIZED(['read_bidder'], this.props.user.user);
      const writeAuthorization = IS_AUTHORIZED(['write_bidder'], this.props.user.user);
      if (!readAuthorization.isAuthorized) {
        return (
          <div>
            <ModelMissingPermission missingPermissions={readAuthorization.missingPermissions} />
          </div>
        );
      }

      return (
        <Rows
          extraLargeHorizontalPadding
          verticalPadding
          gap={spacing.large}
          style={{
            paddingBottom: '96px',
          }}
        >
          <Row noPadding>
            <Rows gap={spacing.medium}>
              <Row noPadding>
                <Text subheader tKey='CurrentTest' />
              </Row>
              {showNewTestButton && (
                <Row noVerticalPadding smallHorizontalPadding>
                  <TextButton onClick={this.setNewTest} disabled={!writeAuthorization.isAuthorized}>
                    {ct('PlusAddItem', { item: ct('ABTest') })}
                  </TextButton>
                </Row>
              )}
              {this.state.NewTest && this.renderNewTest(writeAuthorization.isAuthorized)}
              {showCurrentTest && this.renderCurrentTest(writeAuthorization.isAuthorized)}
            </Rows>
          </Row>
          <Row noPadding>
            <Rows gap={spacing.medium}>
              <Row noPadding>
                <Text subheader>Previous Tests</Text>
              </Row>
              {pastABTests && pastABTests.length > 0 && (
                <Row noPadding>{this.renderPastTests(writeAuthorization.isAuthorized)}</Row>
              )}
            </Rows>
          </Row>
          <SnackbarView
            open={!!this.props.snackbarMessage}
            error={!!this.props.snackbarError}
            onClose={this.props.snackbarClose}
          >
            {this.props.snackbarMessage}
          </SnackbarView>
        </Rows>
      );
    }

    // Refactor redundant props passing
    private renderNewTest = (hasWriteRight: boolean) => {
      return (
        <ABTestPanel
          errors={this.state.errors}
          testType={ABTestMode.New}
          index={0}
          newTest={this.state.NewBTest}
          bidderParams={this.props.bidder}
          ATestShare={this.state.NewATestShare}
          abTestCollapsibleStatus={this.state.testCollapsibleStatus}
          handleCollapse={this.updateCollapseStatus}
          launchTest={this.launchTest}
          handleChange={this.handleChange}
          handleCancel={this.handleCancel}
          disabled={!hasWriteRight}
          publisher={this.props.session.publisher}
          renderFlightDates={this.renderFlighting}
        />
      );
    };

    private renderCurrentTest = (hasWriteRight: boolean) => {
      const currentTest = this.props.currentABTest;
      return (
        <ABTestPanel
          testType={ABTestMode.Current}
          index={currentTest.ID}
          abTest={currentTest}
          bidderParams={this.props.bidder}
          abTestCollapsibleStatus={this.state.testCollapsibleStatus}
          endTest={this.endTest}
          handleCollapse={this.updateCollapseStatus}
          disabled={!hasWriteRight}
          publisher={this.props.session.publisher}
          renderFlightDates={this.renderFlighting}
        />
      );
    };

    private renderPastTests = (hasWriteRight: boolean) => {
      return (
        <Grid>
          {this.props.pastABTests.map((prevTest: IABTesting) => {
            return (
              <ABTestPanel
                testType={ABTestMode.Past}
                index={prevTest.ID}
                abTest={prevTest}
                bidderParams={this.props.bidder}
                abTestCollapsibleStatus={this.state.testCollapsibleStatus}
                implementParameters={this.implementParameters}
                handleCollapse={this.updateCollapseStatus}
                disabled={!hasWriteRight}
                publisher={this.props.session.publisher}
                renderFlightDates={this.renderFlighting}
              />
            );
          })}
        </Grid>
      );
    };
  },
);
